import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'

import { Label } from '../Label'

import * as S from './styles'

const MAX_ANGLE = 180
const START_ANGLE = 90

const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
  const angleInRadians = ((angleInDegrees - START_ANGLE) * Math.PI) / MAX_ANGLE

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  }
}

const describeArc = (x, y, radius, percentage, isStatic) => {
  const endAngle = START_ANGLE + (percentage / 100) * MAX_ANGLE
  const start = polarToCartesian(x, y, radius, endAngle)
  const end = polarToCartesian(x, y, radius, START_ANGLE)

  if (isStatic) return ['M', start.x, start.y, 'A', radius, radius, 0, 0, 1, end.x, end.y].join(' ')

  const staticEndAngle = START_ANGLE + MAX_ANGLE
  const staticStart = polarToCartesian(x, y, radius, staticEndAngle)
  const diffX = start.x - staticStart.x
  const diffY = start.y - staticStart.y

  const drawing = ['M', staticStart.x, staticStart.y, 'A', radius, radius, 0, 0, 1, end.x - diffX, end.y - diffY].join(
    ' '
  )

  return drawing
}
const DRAWING_WIDTH = 94
const DRAWING_HEIGHT = 50
const DRAWING_STROKE = 6
const CORNER_HEIGHT = DRAWING_STROKE / 2
const CENTER_DRAWING_X = DRAWING_WIDTH / 2
const CENTER_DRAWING_Y = DRAWING_HEIGHT - CORNER_HEIGHT
const RADIUS = DRAWING_HEIGHT - DRAWING_STROKE
const NEEDLE_HEIGHT = DRAWING_HEIGHT
const NEEDLE_WIDTH = DRAWING_WIDTH + DRAWING_STROKE
const NEEDLE_STROKE = 3

const getTooltipContent = value => {
  if (value < 90) {
    return 'Complétez votre profil pour améliorer notre estimation de votre taux.'
  }

  return (
    <>
      Pour un taux encore plus précis, réalisez une{' '}
      <S.TooltipLink href="https://app.pretto.fr/">simulation personnalisée</S.TooltipLink>.
    </>
  )
}

const getDrawing = value => {
  const isFull = value === 100
  return describeArc(CENTER_DRAWING_X, CENTER_DRAWING_Y, RADIUS, value, isFull)
}

export const Indicator = ({ value }) => {
  const [pathLength, setPathLength] = useState(0)
  const pathRef = useRef()

  useEffect(() => {
    const length = pathRef.current.getTotalLength()
    setPathLength(length)
  }, [value])

  const fullDrawing = getDrawing(100)

  const drawing = getDrawing(value)

  return (
    <S.Indicator>
      <Label label="Indice de confiance" tooltip={getTooltipContent(value)} />

      <S.SvgContainer $percentage={value} $strokeWidth={DRAWING_STROKE} width={DRAWING_WIDTH} height={DRAWING_HEIGHT}>
        <S.Svg
          width={DRAWING_WIDTH}
          height={DRAWING_HEIGHT}
          viewBox={`0 0 ${DRAWING_WIDTH} ${DRAWING_HEIGHT}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <S.FullPath d={fullDrawing} strokeLinecap="round" strokeWidth={DRAWING_STROKE} />
          <S.Path
            $pathLength={pathLength}
            $percentage={value}
            d={drawing}
            key={`confidence_${value}`}
            ref={pathRef}
            strokeWidth={DRAWING_STROKE}
          />
        </S.Svg>
        <S.Needle
          height={NEEDLE_HEIGHT}
          width={NEEDLE_WIDTH}
          viewBox={`0 0 ${NEEDLE_WIDTH} ${NEEDLE_HEIGHT}`}
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          $rotationAngle={(value / 100) * MAX_ANGLE}
          $strokeWidth={NEEDLE_STROKE}
        >
          <S.NeedlePath
            d={`M ${NEEDLE_WIDTH / 2} ${NEEDLE_HEIGHT - NEEDLE_STROKE} L ${NEEDLE_STROKE} ${
              NEEDLE_HEIGHT - NEEDLE_STROKE
            }`}
            strokeWidth={NEEDLE_STROKE}
            strokeLinecap="round"
          />
        </S.Needle>
      </S.SvgContainer>
    </S.Indicator>
  )
}

Indicator.propTypes = {
  value: PropTypes.number.isRequired,
}
